import { Button, CheckBox, InfoBar } from "@etframework/ui.components";
import { useTranslation } from "react-i18next";
import { slugify } from "../../utils/slugify";
import useModalEvent from "../../hooks/useModalEvent";
import { useCustomFieldGroupByTaskTypeId } from "../../data/queries/customFieldGroupQueries";
import { useRef, useState } from "react";

const Header = () => {
  const { ModalEvents, toggleModal } = useModalEvent();
  const { t, ready } = useTranslation("DataFieldsSection", {
    useSuspense: false,
  });

  if (!ready) return null;

  return (
    <div className="d-flex justify-content-between align-items-center">
      <div className="et-darkest-gray fs-14">{t("Header.Description")}</div>
      <div>
        <Button
          text={t("Header.Action")}
          className="btn-tertiary"
          iconCss="fa-solid fa-plus"
          onClick={() => toggleModal(ModalEvents.CustomFieldGroupModal)}
        />
      </div>
    </div>
  );
};

const DataFieldCategoryButton = ({ text, count, targetRef }) => {
  const handleClick = () => {
    targetRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div
      className="d-flex justify-content-between align-items-center cursor-pointer"
      onClick={handleClick}
    >
      <span>{text}</span>
      <span>{count}</span>
    </div>
  );
};

const DataFieldCategory = ({ title, children, targetRef }) => {
  return (
    <section
      id={`data-fields-category-header--${slugify(title)}`}
      className="et-data-fields-section__custom-field-groups-category space-y-3"
      ref={targetRef}
    >
      <div className="et-data-fields-section__custom-field-groups-category-header">
        <h3 className="et-data-fields-section__custom-field-groups-category-header__title">
          {title}
        </h3>
      </div>
      {children}
    </section>
  );
};

const DataFieldGroupCard = ({ fieldGroup }) => {
  const { t } = useTranslation("DataFieldsSection");
  const { ModalEvents, toggleModal } = useModalEvent();

  return (
    <div
      role="button"
      onClick={() => {
        toggleModal(ModalEvents.CustomFieldGroupModal, {
          groupId: fieldGroup.Id,
          isUpdate: true,
        });
      }}
      className="w-100 d-flex justify-content-between align-items-center shadow-sm et-data-fields-section__custom-field-groups-card"
    >
      <div className="et-data-fields space-y-2">
        <div className="d-flex gap-3">
          <p className="fw-semibold et-data-fields-section__custom-field-groups-card-title">
            {fieldGroup?.Name}
          </p>
          {!fieldGroup?.Visible && (
            <div className="d-flex align-items-center gap-2 et-data-fields-section__custom-field-groups-card-hidden">
              <i className="fa-outline fa-eye-slash text-muted" />
              <span className="text-muted">{t("Inactive")}</span>
            </div>
          )}
        </div>
        {fieldGroup?.CustomFields?.length > 0 && (
          <div
            className="ellipsis-list text-muted"
            title={fieldGroup?.CustomFields?.map((field) => field.Label).join(
              ", "
            )}
          >
            {fieldGroup.CustomFields.map((field, index) => {
              const isLast = index === fieldGroup.CustomFields.length - 1;
              return field.Label + (!isLast ? ", " : "");
            })}
          </div>
        )}
      </div>
      <div className="et-data-fields-section__custom-field-groups-card__actions">
        {!fieldGroup?.Locked && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              toggleModal(ModalEvents.CustomFieldGroupModal, {
                groupId: fieldGroup.Id,
                isUpdate: false,
              });
            }}
            className="et-data-fields-section__custom-field-groups-card__actions-button"
            aria-label="Edit data field group"
          >
            <i className="fa-outline fa-pencil" />
          </button>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggleModal(ModalEvents.CustomFieldGroupModal, {
              groupId: fieldGroup.Id,
              isUpdate: true,
            });
          }}
          className="et-data-fields-section__custom-field-groups-card__actions-button"
          aria-label="Go to data field group"
        >
          <i className="fa-solid fa-arrow-right" />
        </button>
      </div>
    </div>
  );
};

const DataFieldGroups = ({ taskType }) => {
  const { t, ready } = useTranslation("DataFieldsSection", {
    useSuspense: false,
  });
  const [showHidden, setShowHidden] = useState(true);
  const { data: dataFieldGroups } = useCustomFieldGroupByTaskTypeId(
    taskType?.Id
  );

  const targetCFRef = useRef(null);
  const targetEPRef = useRef(null);

  const defaultFieldGroups = dataFieldGroups?.filter((group) => group.Locked);
  const customFieldGroups = dataFieldGroups?.filter((group) => !group.Locked);

  const defaultFieldGroupsCounter = defaultFieldGroups?.length || 0;
  const customFieldGroupsCounter = customFieldGroups?.length || 0;

  const defaultFieldGroupsToShow = showHidden
    ? defaultFieldGroups
    : defaultFieldGroups?.filter((group) => group.Visible);
  const customFieldGroupsToShow = showHidden
    ? customFieldGroups
    : customFieldGroups?.filter((group) => group.Visible);

  if (!ready) return null;

  return (
    <div className="et-data-fields-section__custom-field-groups">
      <div className="row">
        <div className="col-3 p-3">
          <nav className="space-y-3">
            <DataFieldCategoryButton
              text={t("CustomFields")}
              count={customFieldGroupsCounter}
              targetRef={targetCFRef}
            />
            <DataFieldCategoryButton
              text={t("ExpertPracticeFields")}
              count={defaultFieldGroupsCounter}
              targetRef={targetEPRef}
            />
          </nav>
          <hr />
          <CheckBox
            label={t("ShowInactive")}
            className="et-data-fields-section__custom-field-groups__show-all-checkbox"
            id="show-all-custom-fields"
            checked={showHidden}
            change={(e) => {
              setShowHidden(e.checked);
            }}
          />
        </div>
        <div className="col-9">
          <div className="space-y-3 et-data-fields-section__custom-field-groups__category-list">
            <DataFieldCategory
              title={t("CustomFields")}
              targetRef={targetCFRef}
            >
              {customFieldGroupsToShow?.length === 0 && (
                <InfoBar
                  variant="information"
                  iconCss="fa-outline fa-wand-magic-sparkles"
                  message={t("EmptyState", {
                    taskTypeTitle: taskType?.Title,
                  })}
                  rounded
                />
              )}
              {customFieldGroupsToShow?.length > 0 &&
                customFieldGroupsToShow?.map((group) => (
                  <DataFieldGroupCard key={group.Id} fieldGroup={group} />
                ))}
            </DataFieldCategory>
            <DataFieldCategory
              title={t("ExpertPracticeFields")}
              targetRef={targetEPRef}
            >
              {defaultFieldGroupsToShow?.length > 0 &&
                defaultFieldGroupsToShow?.map((group) => (
                  <DataFieldGroupCard key={group.Id} fieldGroup={group} />
                ))}
            </DataFieldCategory>
          </div>
        </div>
      </div>
    </div>
  );
};

export const DataFieldsSection = ({ taskType }) => {
  return (
    <div className="et-data-fields-section">
      <Header />
      <div className="m-2" />
      <DataFieldGroups taskType={taskType} />
    </div>
  );
};
